import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';
import { Building2, AlertTriangle, ShieldCheck, ArrowRight, Layers } from 'lucide-react';

/**
 * Embedded portco pulse for the Alternatives shell.
 *
 * <p>Fits inside the existing dark-glass Alternatives surface and links out to
 * the institutional-grade /portco workspace for deep work. Real data only —
 * pulls from /api/portco and /api/portco/covenants/rollup/portco. No mocks.
 */

interface PortfolioCompany {
  id: number;
  companyName: string;
  sector?: string;
  industry?: string;
  ownershipPct?: number;
  status?: string;
  fundId?: number;
  syntheticCusip?: string;
}

interface PortcoCovenantStatus {
  portcoId: number;
  companyName: string;
  fundId: number;
  severity: 'BREACHED' | 'WARNING' | 'PASSING' | 'NO_DATA';
  totalCovenants: number;
  breached: number;
  warning: number;
  passed: number;
  noData: number;
}

interface PortcoKpi {
  id: number;
  periodEnd: string;
  revenue?: number;
  ebitda?: number;
  netDebt?: number;
}

const Wrap = styled.div`
  padding: 1.5rem 2rem;
  color: #e2e8f0;
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const StatCard = styled.div`
  background: rgba(15, 23, 42, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 1.25rem;
`;

const StatLabel = styled.div`
  font-size: 0.7rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #94a3b8;
  margin-bottom: 0.5rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const StatValue = styled.div`
  font-size: 1.6rem;
  font-weight: 700;
  color: #ffffff;
  font-variant-numeric: tabular-nums;
`;

const StatSub = styled.div`
  font-size: 0.75rem;
  color: #94a3b8;
  margin-top: 0.25rem;
`;

const Section = styled.div`
  background: rgba(30, 41, 59, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
`;

const SectionHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const SectionTitle = styled.h3`
  font-size: 0.95rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Cta = styled.button`
  background: rgba(139, 92, 246, 0.15);
  border: 1px solid rgba(139, 92, 246, 0.4);
  color: #c4b5fd;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 0.4rem 0.8rem;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;

  &:hover {
    background: rgba(139, 92, 246, 0.25);
    border-color: rgba(139, 92, 246, 0.6);
    color: #ddd6fe;
  }
`;

const Row = styled.button`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 0.6fr;
  gap: 1rem;
  align-items: center;
  padding: 0.65rem 0.75rem;
  background: transparent;
  border: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  text-align: left;
  cursor: pointer;
  width: 100%;
  color: inherit;
  font-family: inherit;

  &:hover {
    background: rgba(139, 92, 246, 0.06);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const RowHead = styled(Row)`
  font-size: 0.7rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
  cursor: default;
  font-weight: 600;

  &:hover {
    background: transparent;
  }
`;

const Pill = styled.span<{ $sev: 'BREACHED' | 'WARNING' | 'PASSING' | 'NO_DATA' }>`
  display: inline-block;
  padding: 0.2rem 0.6rem;
  font-size: 0.65rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  font-weight: 700;
  border-radius: 4px;
  background: ${(p) =>
    p.$sev === 'BREACHED'
      ? 'rgba(220, 38, 38, 0.2)'
      : p.$sev === 'WARNING'
      ? 'rgba(217, 119, 6, 0.2)'
      : p.$sev === 'PASSING'
      ? 'rgba(22, 163, 74, 0.2)'
      : 'rgba(148, 163, 184, 0.15)'};
  color: ${(p) =>
    p.$sev === 'BREACHED'
      ? '#fca5a5'
      : p.$sev === 'WARNING'
      ? '#fcd34d'
      : p.$sev === 'PASSING'
      ? '#86efac'
      : '#cbd5e1'};
  border: 1px solid
    ${(p) =>
      p.$sev === 'BREACHED'
        ? 'rgba(220, 38, 38, 0.4)'
        : p.$sev === 'WARNING'
        ? 'rgba(217, 119, 6, 0.4)'
        : p.$sev === 'PASSING'
        ? 'rgba(22, 163, 74, 0.4)'
        : 'rgba(148, 163, 184, 0.25)'};
`;

const Empty = styled.div`
  text-align: center;
  padding: 2rem 1rem;
  color: #94a3b8;
  font-size: 0.85rem;
`;

const Mono = styled.span`
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-variant-numeric: tabular-nums;
`;

const fmtNum = (v?: number) =>
  v == null ? '—' : v.toLocaleString(undefined, { maximumFractionDigits: 0 });
const fmtPct = (v?: number) => (v == null ? '—' : `${v.toFixed(1)}%`);

interface PortcoEmbeddedPanelProps {
  /**
   * When provided, row clicks call this instead of navigating to /portco/:id.
   * Used by PortfolioCompaniesWorkbench to switch the active pill to Detail
   * with portcoId set, keeping users inside the workbench.
   */
  onSelectPortco?: (portcoId: number) => void;
  /**
   * When true, hide the redundant "Open full workspace" and "Covenant heat map"
   * CTAs — those are useful only when this panel is the *standalone* preview,
   * not when it's embedded inside the workbench's Roster pill.
   */
  inWorkbench?: boolean;
}

const PortcoEmbeddedPanel: React.FC<PortcoEmbeddedPanelProps> = ({
  onSelectPortco,
  inWorkbench = false,
}) => {
  const navigate = useNavigate();
  const handleSelect = (id: number) => {
    if (onSelectPortco) onSelectPortco(id);
    else navigate(`/portco/${id}`);
  };
  const [portcos, setPortcos] = useState<PortfolioCompany[]>([]);
  const [rollup, setRollup] = useState<PortcoCovenantStatus[]>([]);
  const [latestKpis, setLatestKpis] = useState<Record<number, PortcoKpi | undefined>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [pcRes, covRes] = await Promise.all([
          axios.get<PortfolioCompany[]>('/api/portco'),
          axios.get<PortcoCovenantStatus[]>('/api/portco/covenants/rollup/portco'),
        ]);
        if (!mounted) return;
        const list = pcRes.data || [];
        setPortcos(list);
        setRollup(covRes.data || []);

        // Latest KPI per portco in one batch round trip (was a request per portco).
        const kpiRes = await axios
          .get<(PortcoKpi & { portcoId: number })[]>('/api/portco/kpi/latest')
          .catch(() => null);
        if (!mounted) return;
        const map: Record<number, PortcoKpi | undefined> = {};
        (kpiRes?.data || []).forEach((kpi) => {
          map[kpi.portcoId] = kpi;
        });
        setLatestKpis(map);
      } catch {
        // surface empty state below
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const breachedCount = rollup.filter((r) => r.severity === 'BREACHED').length;
  const warningCount = rollup.filter((r) => r.severity === 'WARNING').length;
  const totalCovenants = rollup.reduce((s, r) => s + r.totalCovenants, 0);
  const fundCount = useMemo(
    () => new Set(portcos.map((p) => p.fundId).filter(Boolean)).size,
    [portcos],
  );

  const totalLtmEbitda = Object.values(latestKpis).reduce(
    (s, k) => s + (k?.ebitda || 0),
    0,
  );

  // Sort portcos by latest EBITDA desc for "biggest underlyings first"
  const orderedPortcos = useMemo(() => {
    return [...portcos].sort((a, b) => {
      const ea = latestKpis[a.id]?.ebitda || 0;
      const eb = latestKpis[b.id]?.ebitda || 0;
      return eb - ea;
    });
  }, [portcos, latestKpis]);

  const severityFor = (id: number) => rollup.find((r) => r.portcoId === id)?.severity;

  return (
    <Wrap>
      <StatGrid>
        <StatCard>
          <StatLabel>
            <Building2 className="w-3.5 h-3.5" />
            Companies tracked
          </StatLabel>
          <StatValue>{portcos.length}</StatValue>
          <StatSub>across {fundCount} fund{fundCount === 1 ? '' : 's'}</StatSub>
        </StatCard>
        <StatCard>
          <StatLabel>
            <Layers className="w-3.5 h-3.5" />
            LTM EBITDA (look-through)
          </StatLabel>
          <StatValue><Mono>{fmtNum(totalLtmEbitda)}</Mono></StatValue>
          <StatSub>sum of latest portco marks</StatSub>
        </StatCard>
        <StatCard>
          <StatLabel>
            <AlertTriangle className="w-3.5 h-3.5" />
            Covenants breached
          </StatLabel>
          <StatValue style={{ color: breachedCount > 0 ? '#f87171' : '#ffffff' }}>
            {breachedCount}
          </StatValue>
          <StatSub>{warningCount} warning · {totalCovenants} monitored</StatSub>
        </StatCard>
        {!inWorkbench && (
          <StatCard>
            <StatLabel>
              <ShieldCheck className="w-3.5 h-3.5" />
              Workspace
            </StatLabel>
            <Cta onClick={() => navigate('/portco')}>
              Open full workspace
              <ArrowRight className="w-3.5 h-3.5" />
            </Cta>
            <StatSub>institutional-grade detail surface</StatSub>
          </StatCard>
        )}
      </StatGrid>

      <Section>
        <SectionHead>
          <SectionTitle>
            <Building2 className="w-4 h-4 text-violet-300" />
            Portfolio companies — by latest EBITDA
          </SectionTitle>
          {!inWorkbench && (
            <Cta onClick={() => navigate('/portfolio-management?tab=credit-loans&sub=covenants&scope=PORTCO_DEBT')}>
              Covenant Center
              <ArrowRight className="w-3.5 h-3.5" />
            </Cta>
          )}
        </SectionHead>

        {loading ? (
          <Empty>Loading…</Empty>
        ) : orderedPortcos.length === 0 ? (
          <Empty>
            No portfolio companies yet. Upload a portco financials document or create one in the
            workspace.
          </Empty>
        ) : (
          <>
            <RowHead as="div">
              <span>Company</span>
              <span>Sector</span>
              <span style={{ textAlign: 'right' }}>Ownership</span>
              <span style={{ textAlign: 'right' }}>LTM EBITDA</span>
              <span>Status</span>
            </RowHead>
            {orderedPortcos.slice(0, 8).map((p) => {
              const sev = severityFor(p.id);
              const kpi = latestKpis[p.id];
              return (
                <Row key={p.id} onClick={() => handleSelect(p.id)}>
                  <span style={{ color: '#e2e8f0', fontWeight: 500 }}>{p.companyName}</span>
                  <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>{p.sector || '—'}</span>
                  <Mono style={{ color: '#cbd5e1', textAlign: 'right' }}>{fmtPct(p.ownershipPct)}</Mono>
                  <Mono style={{ color: '#cbd5e1', textAlign: 'right' }}>{fmtNum(kpi?.ebitda)}</Mono>
                  {sev ? <Pill $sev={sev}>{sev.replace('_', ' ')}</Pill> : <span style={{ color: '#475569' }}>—</span>}
                </Row>
              );
            })}
          </>
        )}
      </Section>
    </Wrap>
  );
};

export default PortcoEmbeddedPanel;
